import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GitCompareArrows,
  Plus,
  Star,
  Trash2,
  Edit3,
  Play,
  Sparkles,
  Loader2,
  Link as LinkIcon,
  Search as SearchIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CollapsibleFiltersPanel from "@/components/CollapsibleFiltersPanel";
import comparativesApi from "@/api/comparatives";
import { useAuth } from "@/contexts/AuthContext";
import ComparativeBuilder from "@/components/comparatives/ComparativeBuilder";
import ComparativeChart from "@/components/comparatives/ComparativeChart";
import ShareComparativeDialog from "@/components/comparatives/ShareComparativeDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Phase 5.4 — Comparatives standalone module.
 *
 * Replaces the old "Comparativo de Tipos" tab inside ReportsHub with a
 * dedicated CRUD-able space. Includes:
 * - Preset cards (PICK vs STAGE, TRNS vs RCVR — read-only).
 * - The user's saved comparatives.
 * - A builder modal that lets you configure 1..5 transaction types.
 * - Inline preview chart for the currently-selected comparative.
 *
 * The page is the new home for `/comparatives`. The legacy share links
 * pointing at `/reports/type-comparison` keep working via a redirect on
 * `TypeComparison.jsx`.
 */
export default function ComparativesList() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [runError, setRunError] = useState(null);
  const [running, setRunning] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [sharing, setSharing] = useState(null); // comparative being shared

  // Phase 5.9 — local filters (search, only starred, kind segmented).
  // Debounce search so we don't re-filter on every keystroke.
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [onlyStarred, setOnlyStarred] = useState(false);
  const [kind, setKind] = useState("all"); // all | presets | mine
  useEffect(() => {
    const id = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  const userEmail = (user?.email || "").toLowerCase();

  const fetchList = async () => {
    try {
      const r = await comparativesApi.list();
      setItems(r.data.items || []);
    } catch (err) {
      toast.error("Error cargando comparativos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, []);

  const runComparative = async (c) => {
    setSelected(c);
    setRunResult(null);
    setRunError(null);
    setRunning(true);
    try {
      const r = await comparativesApi.runSaved(c.id);
      setRunResult(r.data.result);
    } catch (err) {
      // Surface a persistent, actionable message in the panel — the toast
      // alone is easy to miss and the placeholder ("Selecciona un
      // comparativo") is misleading when one is already selected.
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "No se pudo ejecutar el comparativo.";
      setRunError(String(detail));
      toast.error("Error ejecutando comparativo");
    } finally {
      setRunning(false);
    }
  };

  const handleToggleStar = async (c, e) => {
    e.stopPropagation();
    try {
      await comparativesApi.toggleStar(c.id);
      fetchList();
    } catch {
      toast.error("Error guardando favorito");
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await comparativesApi.remove(confirmDelete.id);
      toast.success("Comparativo eliminado");
      if (selected?.id === confirmDelete.id) {
        setSelected(null);
        setRunResult(null);
        setRunError(null);
      }
      setConfirmDelete(null);
      fetchList();
    } catch {
      toast.error("No se pudo eliminar");
    }
  };

  const handleSaved = (savedDoc) => {
    setBuilderOpen(false);
    setEditing(null);
    fetchList();
    if (savedDoc) {
      // Open chart for the freshly-saved comparative.
      runComparative(savedDoc);
    }
  };

  const presets = useMemo(() => items.filter((c) => c.is_preset), [items]);
  const mine = useMemo(() => items.filter((c) => !c.is_preset), [items]);

  // Apply local filters: kind segmented, only-starred, debounced search.
  const filterFn = (c) => {
    if (onlyStarred && !c.starred) return false;
    if (search) {
      const haystack = `${c.name || ""} ${c.description || ""}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }
    return true;
  };
  const visiblePresets = useMemo(
    () => (kind === "mine" ? [] : presets.filter(filterFn)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presets, kind, onlyStarred, search]
  );
  const visibleMine = useMemo(
    () => (kind === "presets" ? [] : mine.filter(filterFn)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mine, kind, onlyStarred, search]
  );

  // Active filter counter (kind-not-all + onlyStarred + search) — same pattern
  // as /catalog so the badge stays consistent across the app.
  const activeFilterCount =
    (kind !== "all" ? 1 : 0) + (onlyStarred ? 1 : 0) + (search ? 1 : 0);

  const resetFilters = () => {
    setSearchInput("");
    setSearch("");
    setOnlyStarred(false);
    setKind("all");
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1
            className="flex items-center gap-2 text-2xl font-semibold tracking-tight"
            data-testid="comparatives-page-title"
          >
            <GitCompareArrows className="h-6 w-6 text-primary" />
            Comparativos
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Compara hasta 5 tipos de transacción a lo largo del tiempo. Guarda
            tus configuraciones favoritas para reutilizarlas.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setBuilderOpen(true);
          }}
          data-testid="comparatives-new-btn"
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" />
          Nuevo comparativo
        </Button>
      </div>

      {loading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
        </div>
      ) : (
        <>
          {/* Phase 5.9 — Collapsible filters panel (search + only starred + kind). */}
          <CollapsibleFiltersPanel
            title={t("filters.title", { defaultValue: "Filtros" })}
            activeCount={activeFilterCount}
            testId="comparatives-filters-panel"
          >
            <div className="space-y-2 p-3" data-testid="comparatives-filters-body">
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="relative sm:col-span-2">
                  <SearchIcon className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Buscar por nombre o descripción…"
                    className="h-8 pl-7 text-xs"
                    data-testid="comparatives-search-input"
                  />
                </div>
                <label
                  className="flex h-8 cursor-pointer select-none items-center gap-2 rounded-md border border-border bg-background/40 px-2 text-xs"
                  data-testid="comparatives-only-starred-label"
                >
                  <Checkbox
                    checked={onlyStarred}
                    onCheckedChange={(v) => setOnlyStarred(!!v)}
                    data-testid="comparatives-only-starred"
                  />
                  <Star className="h-3.5 w-3.5 text-amber-500" />
                  Solo destacados
                </label>
              </div>
              <Tabs value={kind} onValueChange={setKind}>
                <TabsList className="h-7" data-testid="comparatives-kind-tabs">
                  <TabsTrigger value="all" className="text-[11px]" data-testid="kind-all">
                    Todos
                  </TabsTrigger>
                  <TabsTrigger value="presets" className="text-[11px]" data-testid="kind-presets">
                    Presets
                  </TabsTrigger>
                  <TabsTrigger value="mine" className="text-[11px]" data-testid="kind-mine">
                    Míos
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  data-testid="comparatives-filters-reset"
                  className="text-[11px] text-primary hover:underline"
                >
                  Restablecer filtros
                </button>
              )}
            </div>
          </CollapsibleFiltersPanel>

          {/* Presets */}
          {visiblePresets.length > 0 && (
            <section data-testid="presets-section">
              <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                <Sparkles className="h-3 w-3" />
                Presets
              </h2>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {visiblePresets.map((c) => (
                  <ComparativeCard
                    key={c.id}
                    c={c}
                    selected={selected?.id === c.id}
                    onClick={() => runComparative(c)}
                    onStar={(e) => handleToggleStar(c, e)}
                    onShare={(e) => {
                      e.stopPropagation();
                      setSharing(c);
                    }}
                    readonly
                  />
                ))}
              </div>
            </section>
          )}

          {/* User-owned */}
          {kind !== "presets" && (
            <section data-testid="mine-section">
              <h2 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Mis comparativos
              </h2>
              {visibleMine.length === 0 ? (
                <div
                  className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground"
                  data-testid="mine-empty"
                >
                  {mine.length === 0
                    ? "Aún no has guardado ninguno. Usa “Nuevo comparativo” para crear el primero."
                    : "Ningún comparativo coincide con los filtros actuales."}
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                  {visibleMine.map((c) => (
                    <ComparativeCard
                      key={c.id}
                      c={c}
                      selected={selected?.id === c.id}
                      onClick={() => runComparative(c)}
                      onStar={(e) => handleToggleStar(c, e)}
                      onShare={(e) => {
                        e.stopPropagation();
                        setSharing(c);
                      }}
                      onEdit={(e) => {
                        e.stopPropagation();
                        setEditing(c);
                        setBuilderOpen(true);
                      }}
                      onDelete={(e) => {
                        e.stopPropagation();
                        setConfirmDelete(c);
                      }}
                      canEdit={c.created_by === userEmail}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* Active view */}
          {selected && (
            <section data-testid="active-view">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="text-base font-semibold">
                  {selected.name}
                  {selected.is_preset && (
                    <Badge variant="outline" className="ml-2 text-[10px]">
                      preset
                    </Badge>
                  )}
                </h2>
                {running && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
              {selected.description && (
                <p className="mb-2 text-xs text-muted-foreground">
                  {selected.description}
                </p>
              )}
              <ComparativeChart result={runResult} loading={running} error={runError} />
            </section>
          )}
        </>
      )}

      {/* Builder modal */}
      <ComparativeBuilder
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setEditing(null);
        }}
        editing={editing}
        onSaved={handleSaved}
      />

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="delete-comparative-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar comparativo?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmDelete?.name} se eliminará permanentemente. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              data-testid="confirm-delete-comparative"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share dialog */}
      <ShareComparativeDialog
        open={!!sharing}
        comparative={sharing}
        onClose={() => setSharing(null)}
        onChanged={() => fetchList()}
      />
    </div>
  );
}

function ComparativeCard({
  c,
  selected,
  onClick,
  onStar,
  onShare,
  onEdit,
  onDelete,
  canEdit = false,
  readonly = false,
}) {
  const types = c.config?.types || [];
  return (
    <Card
      onClick={onClick}
      data-testid={`comparative-card-${c.id}`}
      className={
        "group relative cursor-pointer transition-all hover:border-primary/60 " +
        (selected ? "ring-2 ring-primary" : "")
      }
    >
      <CardContent className="space-y-2 p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold">{c.name}</div>
            {c.description && (
              <div className="line-clamp-2 text-[11px] text-muted-foreground">
                {c.description}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onStar}
            data-testid={`star-${c.id}`}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-amber-500"
            title={c.starred ? "Quitar destacado" : "Marcar como destacado"}
          >
            <Star
              className={"h-3.5 w-3.5 " + (c.starred ? "fill-amber-400 text-amber-400" : "")}
            />
          </button>
        </div>
        <div className="flex flex-wrap gap-1">
          {types.map((t) => (
            <Badge
              key={t}
              variant="outline"
              className="font-mono text-[10px]"
            >
              {t}
            </Badge>
          ))}
        </div>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
          <span>{c.config?.granularity || "month"}</span>
          <span>·</span>
          <span>{c.config?.metric === "tx_per_hour" ? "tx/hr" : "count"}</span>
          {c.config?.breakdown !== "total" && (
            <>
              <span>·</span>
              <span>{c.config.breakdown}</span>
            </>
          )}
        </div>
        <div className="flex items-center gap-1 border-t border-border pt-1.5">
          <button
            type="button"
            onClick={onClick}
            data-testid={`run-${c.id}`}
            className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium text-primary hover:bg-primary/10"
          >
            <Play className="h-3 w-3" />
            Ejecutar
          </button>
          {onShare && (
            <button
              type="button"
              onClick={onShare}
              data-testid={`share-${c.id}`}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted"
              title="Compartir enlace público"
            >
              <LinkIcon className="h-3 w-3" />
              Compartir
              {c.share_token && !c.share_revoked && (
                <span
                  className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-emerald-500"
                  data-testid={`share-active-${c.id}`}
                  title="Enlace activo"
                />
              )}
            </button>
          )}
          {canEdit && !readonly && (
            <>
              <button
                type="button"
                onClick={onEdit}
                data-testid={`edit-${c.id}`}
                className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground hover:bg-muted"
              >
                <Edit3 className="h-3 w-3" />
                Editar
              </button>
              <button
                type="button"
                onClick={onDelete}
                data-testid={`delete-${c.id}`}
                className="ml-auto inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] text-red-500 hover:bg-red-500/10"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
